import React, { useState, useEffect, useRef } from 'react';
import { Podcast, AdConfig, CertificationType } from '../../types';
import { StorageService } from '../../services/storageService';
import { 
    Mic2, Plus, Trash2, Edit, Save, X, 
    Search, Globe, Link, 
    Code, Image as ImageIcon, Clock, Layout, 
    Layers, AlertTriangle, ExternalLink,
    Loader2, Sparkles, Headphones,
    Rss, Play, ListMusic, Calendar, Info, ChevronRight, Radio, Pause, Volume2,
    Monitor, Smartphone, FileAudio, FileCode, CheckCircle,
    Eye, FastForward, Award
} from 'lucide-react';
import { SponsoredService } from '../../services/sponsoredService';
import { PageLoader } from '../../components/PageLoader';

interface RSSEpisode {
    title: string;
    description: string;
    imageUrl: string;
    sourceUrl: string;
    duration: string;
    pubDate: string;
}

export const PodcastManager: React.FC = () => {
    const [certId, setCertId] = useState('cna');
    const [certs, setCerts] = useState<CertificationType[]>([]);
    const [podcasts, setPodcasts] = useState<Podcast[]>([]);
    const [selectedPodcast, setSelectedPodcast] = useState<Partial<Podcast> | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [globalConfig, setGlobalConfig] = useState(SponsoredService.getConfig());
    const [status, setStatus] = useState<{type: 'error' | 'success', text: string} | null>(null);

    // RSS Discovery State
    const [rssUrl, setRssUrl] = useState('');
    const [isParsingRss, setIsParsingRss] = useState(false);
    const [isBulkImporting, setIsBulkImporting] = useState(false);
    const [rssEpisodes, setRssEpisodes] = useState<RSSEpisode[]>([]);
    const [channelMeta, setChannelMeta] = useState<{title: string, desc: string, img: string} | null>(null);
    const [rssError, setRssError] = useState<string | null>(null);
    
    // Preview Player State
    const [previewAudio, setPreviewAudio] = useState<{url: string, title: string} | null>(null);
    const previewRef = useRef<HTMLAudioElement | null>(null);

    useEffect(() => {
        StorageService.getCertifications().then(data => {
            setCerts(data);
            if (data.length > 0) setCertId(data[0].id);
        });
    }, []);

    useEffect(() => {
        if (certId) loadData();
    }, [certId]);

    const loadData = async () => {
        setIsLoading(true);
        setStatus(null);
        try {
            const data = await StorageService.getPodcasts(certId, true);
            setPodcasts(data);
        } catch (e: any) {
            console.error(e);
            setStatus({ type: 'error', text: e.message || 'Error loading audio library.' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleFetchRss = async () => {
        if (!rssUrl.trim()) return;
        setIsParsingRss(true);
        setRssError(null);
        setRssEpisodes([]);
        setChannelMeta(null);

        try {
            // FIXED: Path includes service prefix for GCP routing
            const response = await fetch(`/practice-exams/api/proxy/rss?url=${encodeURIComponent(rssUrl)}`);
            if (!response.ok) throw new Error(`External source returned error: ${response.status}`);
            
            const xmlText = await response.text();
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(xmlText, "text/xml");
            
            if (xmlDoc.getElementsByTagName("parsererror").length > 0) {
                throw new Error("Registry parse error: Invalid RSS/XML structure.");
            }

            const getTag = (el: Element | Document, name: string) => {
                return el.querySelector(name)?.textContent || 
                       el.getElementsByTagName(`itunes:${name}`)[0]?.textContent || "";
            };

            const getAttr = (el: Element | Document, name: string, attr: string) => {
                const standard = el.querySelector(name);
                if (standard && standard.getAttribute(attr)) return standard.getAttribute(attr);
                const itunes = el.getElementsByTagName(`itunes:${name}`)[0];
                return itunes ? itunes.getAttribute(attr) : "";
            };

            const channelTitle = getTag(xmlDoc, "title") || "Unknown Series";
            const channelDesc = (getTag(xmlDoc, "description") || getTag(xmlDoc, "summary")).replace(/<[^>]*>?/gm, '');
            
            const channelImg = xmlDoc.querySelector("channel > image > url")?.textContent || 
                               getAttr(xmlDoc.querySelector("channel") as Element, "image", "href") || "";

            setChannelMeta({ title: channelTitle, desc: channelDesc, img: channelImg });

            const items = xmlDoc.querySelectorAll("item");
            const episodes: RSSEpisode[] = Array.from(items).map(item => ({
                title: getTag(item, "title") || "Untitled Episode",
                description: (getTag(item, "description") || getTag(item, "summary")).replace(/<[^>]*>?/gm, ''),
                sourceUrl: item.querySelector("enclosure")?.getAttribute("url") || "",
                pubDate: getTag(item, "pubDate"),
                duration: getTag(item, "duration") || "--:--",
                imageUrl: getAttr(item, "image", "href") || channelImg
            }));

            if (episodes.length === 0) throw new Error("Registry find error: No items found in this feed.");
            setRssEpisodes(episodes);
        } catch (err: any) {
            console.error("RSS Ingestion Error:", err);
            setRssError(err.message || "Failed to parse feed. Try a different RSS source.");
        } finally {
            setIsParsingRss(false);
        }
    };

    const handleBulkImportAll = async () => {
        if (rssEpisodes.length === 0) return;
        if (!confirm(`This will import all ${rssEpisodes.length} episodes into the ${certId.toUpperCase()} registry. Proceed?`)) return;

        setIsBulkImporting(true);
        setStatus(null);
        try {
            const batch: Partial<Podcast>[] = rssEpisodes.map((ep, idx) => ({
                title: ep.title,
                description: ep.description,
                sourceType: 'rss',
                sourceUrl: ep.sourceUrl,
                imageUrl: ep.imageUrl,
                duration: ep.duration,
                isActive: true,
                order: podcasts.length + idx
            }));

            const result = await StorageService.savePodcastsBulk(certId, batch);
            setStatus({ type: 'success', text: `Mass ingestion complete: ${result.count} tracks indexed into ${certId.toUpperCase()}.` });
            setRssEpisodes([]);
            setRssUrl('');
            setChannelMeta(null);
            loadData();
        } catch (e: any) {
            setStatus({ type: 'error', text: 'Audio registry sync failed: ' + (e.message || 'Check database connectivity.') });
        } finally {
            setIsBulkImporting(false);
        }
    };

    const linkFullFeed = () => {
        if (!channelMeta) return;
        setSelectedPodcast({
            certId,
            title: channelMeta.title,
            description: channelMeta.desc,
            sourceType: 'rss',
            rssUrl: rssUrl,
            isRssFeed: true,
            autoSync: true,
            imageUrl: channelMeta.img,
            isActive: true,
            order: podcasts.length
        });
        setRssEpisodes([]);
        setRssUrl('');
        setStatus({ type: 'success', text: 'Series metadata linked. Review and save to commit.' });
    };

    const importEpisode = (ep: RSSEpisode) => {
        setSelectedPodcast({
            certId,
            title: ep.title,
            description: ep.description,
            sourceType: 'rss',
            sourceUrl: ep.sourceUrl,
            imageUrl: ep.imageUrl,
            duration: ep.duration,
            isActive: true,
            order: podcasts.length
        });
        setRssEpisodes([]);
        setRssUrl('');
        setStatus({ type: 'success', text: 'Episode metadata mapped. Review and save to commit.' });
    };

    const handleSavePodcast = async () => {
        if (!selectedPodcast?.title) return;
        setIsSaving(true);
        setStatus(null);
        try {
            await StorageService.savePodcast(certId, selectedPodcast);
            setSelectedPodcast(null);
            setStatus({ type: 'success', text: 'Registry synchronized successfully.' });
            loadData();
        } catch (e: any) {
            setStatus({ type: 'error', text: 'Synchronization protocol failure: ' + (e.message || 'Verify server endpoints.') });
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (confirm('Permanently purge this audio asset from the registry?')) {
            try {
                // Fixed StorageService.deletePodcast call to include certId as the first argument as required by the service definition.
                await StorageService.deletePodcast(certId, id);
                loadData();
                setStatus({ type: 'success', text: 'Asset purged from cloud.' });
            } catch (e: any) {
                setStatus({ type: 'error', text: 'Purge failed: ' + e.message });
            }
        }
    };

    const filteredList = podcasts.filter(p => p.title.toLowerCase().includes(searchTerm.toLowerCase()));

    const ToggleLeft = ({size}: {size: number}) => (
        <svg width={size} height={size/2} viewBox="0 0 48 24" fill="none"><rect width="48" height="24" rx="12" fill="currentColor" fillOpacity="0.1"/><circle cx="12" cy="12" r="8" fill="white" shadow-lg="true"/></svg>
    );

    const ToggleRight = ({size}: {size: number}) => (
        <svg width={size} height={size/2} viewBox="0 0 48 24" fill="none"><rect width="48" height="24" rx="12" fill="currentColor"/><circle cx="36" cy="12" r="8" fill="white"/></svg>
    );

    return (
        <div className="space-y-8 animate-fade-in pb-24 h-full flex flex-col text-slate-900">
            <audio ref={previewRef} src={previewAudio?.url || ''} onEnded={() => setPreviewAudio(null)} />
            
            <div className="flex flex-col lg:flex-row justify-between items-stretch gap-6">
                <div className="flex-1 bg-slate-900 p-8 rounded-[2.5rem] shadow-2xl border border-white/5 relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:scale-110 transition-transform duration-1000 pointer-events-none">
                        <Mic2 size={160} />
                    </div>
                    <div className="relative z-10">
                        <h2 className="text-3xl font-black text-white tracking-tight flex items-center gap-3">
                            <Headphones size={24} className="text-indigo-400" /> Podcast Engine
                        </h2>
                        <p className="text-slate-400 font-medium text-sm mt-2 max-w-xl leading-relaxed">
                            Publish expert-led clinical series via RSS or create custom standalone tracks using raw audio links and HTML embed codes.
                        </p>
                        
                        <div className="mt-8 flex flex-wrap gap-3">
                            <div className="bg-white/5 p-1 rounded-2xl border border-white/10 flex items-center shrink-0">
                                <Award className="ml-3 text-indigo-400" size={16} />
                                <select 
                                    value={certId} 
                                    onChange={e => setCertId(e.target.value)} 
                                    className="bg-transparent text-white px-4 py-2 rounded-xl outline-none font-black text-[10px] cursor-pointer"
                                >
                                    {certs.map(c => <option key={c.id} value={c.id} className="text-black">{c.shortName}</option>)}
                                </select>
                            </div>
                            <button 
                                onClick={() => setSelectedPodcast({ certId, title: 'New Audio Asset', sourceType: 'rss', isActive: true, order: podcasts.length })}
                                className="px-8 py-4 bg-indigo-600 text-white rounded-2xl font-black text-[10px] tracking-widest flex items-center gap-2 shadow-xl hover:bg-indigo-50 transition-all active:scale-[0.95] border-b-4 border-indigo-900"
                            >
                                <Plus size={18} /> Provision Asset
                            </button>
                        </div>
                    </div>
                </div>

                <div className="w-full lg:w-96 bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm flex flex-col justify-between">
                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <span className="text-[10px] font-black text-slate-400 tracking-widest">Global Status</span>
                            <span className={`text-[9px] font-black px-2 py-0.5 rounded-md ${globalConfig.isActive ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
                                {globalConfig.isActive ? 'Network Online' : 'Network Restricted'}
                            </span>
                        </div>
                        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100">
                            <div className="flex items-center gap-3">
                                <button onClick={() => {
                                    const next = { ...globalConfig, isPodcastsEnabled: !globalConfig.isPodcastsEnabled };
                                    setGlobalConfig(next);
                                    SponsoredService.updateConfig(next);
                                }} className={`transition-all ${globalConfig.isPodcastsEnabled ? 'text-medical-600' : 'text-slate-300'}`}>
                                    {globalConfig.isPodcastsEnabled ? <ToggleRight size={48} /> : <ToggleLeft size={48} />}
                                </button>
                                <span className="text-11px font-black text-slate-700 tracking-tight">Audio Hub Visibility</span>
                            </div>
                        </div>
                    </div>
                    <p className="text-[8px] text-slate-400 font-bold leading-relaxed px-1 mt-4">
                        "Toggling this will hide the entire Podcast module from student navigation menus globally."
                    </p>
                </div>
            </div>

            {status && (
                <div className={`p-4 rounded-3xl flex items-center justify-between animate-pop border-2 shadow-xl ${status.type === 'error' ? 'bg-rose-50 border-rose-100 text-rose-700' : 'bg-emerald-50 border-emerald-100 text-emerald-700'}`}>
                    <div className="flex items-center gap-3 ml-2">
                        {status.type === 'error' ? <AlertTriangle size={18}/> : <CheckCircle size={18}/>}
                        <span className="font-black text-xs tracking-widest">{status.text}</span>
                    </div>
                    <button onClick={() => setStatus(null)} className="p-2 hover:bg-black/5 rounded-xl transition-all"><X size={16}/></button>
                </div>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 flex-1 min-h-0">
                {/* LIST PANEL */}
                <div className="xl:col-span-4 bg-white rounded-[2.5rem] border border-slate-200 overflow-hidden flex flex-col shadow-sm">
                    <div className="p-6 bg-slate-50 border-b border-slate-100 flex items-center gap-4">
                        <div className="relative flex-1 w-full group">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors" size={16} />
                            <input 
                                type="text" 
                                placeholder={`Filter ${certId.toUpperCase()} catalog...`} 
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="w-full pl-11 pr-4 py-3 bg-white border border-slate-200 rounded-xl outline-none font-bold text-xs focus:ring-4 focus:ring-indigo-500/5 transition-all shadow-inner"
                            />
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 custom-scrollbar space-y-2 bg-slate-50/30">
                        {isLoading ? (
                            <PageLoader message="Hydrating Catalog..." compact />
                        ) : filteredList.length === 0 ? (
                            <div className="py-20 flex flex-col items-center justify-center text-slate-300 opacity-40 gap-4 grayscale scale-90">
                                <Radio size={64} strokeWidth={1.5} />
                                <p className="font-black text-sm tracking-widest">No assets found</p>
                            </div>
                        ) : filteredList.map(p => (
                            <div 
                                key={p._id} 
                                onClick={() => { setSelectedPodcast(p); setStatus(null); }}
                                className={`p-4 rounded-2xl border-2 transition-all cursor-pointer group flex items-center justify-between ${selectedPodcast?._id === p._id ? 'bg-indigo-50 border-indigo-500 shadow-md translate-x-1' : 'bg-white border-transparent hover:bg-slate-50 shadow-sm'}`}
                            >
                                <div className="flex items-center gap-4 overflow-hidden">
                                    <div className={`w-12 h-12 rounded-xl overflow-hidden shadow-sm shrink-0 bg-slate-100 border-2 transition-all ${selectedPodcast?._id === p._id ? 'border-indigo-200' : 'border-slate-50'}`}>
                                        <img src={p.imageUrl || 'https://images.unsplash.com/photo-1478737270239-2fccd27ee8f9?auto=format&fit=crop&q=80&w=200'} className="w-full h-full object-cover" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <p className="font-black text-[10px] text-slate-800 truncate leading-tight">{p.title}</p>
                                            {p.isRssFeed && <div className="p-1 bg-indigo-100 text-indigo-600 rounded-md" title="Series Channel"><Rss size={10}/></div>}
                                            {p.sourceType === 'embed' && <div className="p-1 bg-medical-100 text-medical-600 rounded-md" title="Embedded Track"><Code size={10}/></div>}
                                        </div>
                                        <p className="text-[8px] font-black text-slate-400 tracking-widest flex items-center gap-1.5 mt-0.5">
                                            {p.isRssFeed ? <Globe size={10} className="text-indigo-400" /> : <FileAudio size={10} className="text-medical-400" />} 
                                            {p.isRssFeed ? 'CHANNEL' : 'SINGLE TRACK'}
                                        </p>
                                    </div>
                                </div>
                                <button onClick={(e) => { e.stopPropagation(); handleDelete(p._id); }} className="p-2 text-slate-300 hover:text-rose-500 rounded-lg transition-all opacity-0 group-hover:opacity-100"><Trash2 size={16}/></button>
                            </div>
                        ))}
                    </div>
                </div>

                {/* EDITOR PANEL */}
                {selectedPodcast ? (
                    <div className="xl:col-span-8 bg-white rounded-[2.5rem] border border-slate-200 shadow-sm flex flex-col min-h-0 overflow-hidden relative">
                        <div className="flex-1 overflow-y-auto p-8 md:p-10 custom-scrollbar space-y-12">
                            <div className="flex justify-between items-start border-b border-slate-50 pb-8">
                                <div>
                                    <h3 className="text-2xl font-black text-slate-900 tracking-tight">Audio Asset Orchestrator</h3>
                                    <p className="text-[10px] font-black text-slate-400 tracking-widest mt-1">Configuring {selectedPodcast.isRssFeed ? 'Series Feed' : 'Custom Track'} for {certId.toUpperCase()}</p>
                                </div>
                                <button onClick={() => setSelectedPodcast(null)} className="p-3 bg-slate-50 text-slate-400 rounded-2xl hover:text-rose-500 transition-all shadow-sm border border-slate-100"><X size={24} /></button>
                            </div>

                            {/* SOURCE MODE SWITCHER */}
                            <div className="space-y-6">
                                <div className="flex items-center gap-2 mb-2">
                                    <Layers size={14} className="text-indigo-600" />
                                    <label className="text-[10px] font-black text-slate-400 tracking-widest">Logic Ingestion Mode</label>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <button 
                                        onClick={() => setSelectedPodcast({...selectedPodcast, isRssFeed: true, sourceType: 'rss'})}
                                        className={`p-6 rounded-[2rem] border-2 text-left transition-all flex flex-col gap-3 group active:scale-[0.98] ${selectedPodcast.isRssFeed ? 'bg-white border-indigo-500 shadow-xl ring-4 ring-indigo-500/5' : 'bg-slate-50 border-slate-100 opacity-60'}`}
                                    >
                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${selectedPodcast.isRssFeed ? 'bg-indigo-600 text-white shadow-lg rotate-3' : 'bg-slate-200 text-slate-400'}`}>
                                            <Rss size={20}/>
                                        </div>
                                        <div>
                                            <p className="font-black text-xs tracking-tight text-slate-800">Automated Feed (RSS)</p>
                                            <p className="text-[9px] font-bold text-slate-400 leading-relaxed mt-1">Sync an entire series from Apple, Spotify, or Anchor.</p>
                                        </div>
                                    </button>
                                    <button 
                                        onClick={() => setSelectedPodcast({...selectedPodcast, isRssFeed: false, sourceType: 'embed'})}
                                        className={`p-6 rounded-[2rem] border-2 text-left transition-all flex flex-col gap-3 group active:scale-[0.98] ${!selectedPodcast.isRssFeed ? 'bg-white border-medical-500 shadow-xl ring-4 ring-medical-500/5' : 'bg-slate-50 border-slate-100 opacity-60'}`}
                                    >
                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${!selectedPodcast.isRssFeed ? 'bg-medical-600 text-white shadow-lg -rotate-3' : 'bg-slate-200 text-slate-400'}`}>
                                            <Code size={20}/>
                                        </div>
                                        <div>
                                            <p className="font-black text-xs tracking-tight text-slate-800">Custom Track (Manual)</p>
                                            <p className="text-[9px] font-bold text-slate-400 leading-relaxed mt-1">Paste a single MP3 URL or a raw HTML embed iframe code.</p>
                                        </div>
                                    </button>
                                </div>
                            </div>

                            {/* RSS INGESTION PANEL */}
                            {selectedPodcast.isRssFeed && (
                                <div className="bg-indigo-50/50 p-8 rounded-[2.5rem] border-2 border-indigo-100 space-y-8 animate-slide-up">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <Globe size={18} className="text-indigo-600" />
                                            <h4 className="text-sm font-black text-indigo-950 tracking-widest">Feed Synchronization</h4>
                                        </div>
                                        <div className="flex items-center gap-3 px-3 py-1.5 bg-white rounded-xl border border-indigo-100">
                                            <span className="text-[8px] font-black text-slate-500">Auto-Refresh</span>
                                            <button onClick={() => setSelectedPodcast({...selectedPodcast, autoSync: !selectedPodcast.autoSync})} className={`transition-all ${selectedPodcast.autoSync ? 'text-indigo-500' : 'text-slate-300'}`}>
                                                {selectedPodcast.autoSync ? <ToggleRight size={32} /> : <ToggleLeft size={32} />}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="flex gap-3">
                                        <div className="relative flex-1 group">
                                            <Link className="absolute left-4 top-1/2 -translate-y-1/2 text-indigo-300" size={18} />
                                            <input 
                                                type="text" 
                                                placeholder="Paste podcast RSS URL..." 
                                                value={rssUrl}
                                                onChange={e => setRssUrl(e.target.value)}
                                                className="w-full pl-12 pr-4 py-4 bg-white border border-indigo-100 rounded-2xl outline-none font-bold text-xs focus:ring-4 focus:ring-indigo-500/10 shadow-inner transition-all"
                                            />
                                        </div>
                                        <button 
                                            onClick={handleFetchRss}
                                            disabled={isParsingRss || !rssUrl.trim()}
                                            className="px-8 py-4 bg-indigo-600 text-white rounded-2xl font-black text-[10px] tracking-widest flex items-center gap-2 hover:bg-indigo-700 transition-all shadow-xl disabled:opacity-50 active:scale-[0.95]"
                                        >
                                            {isParsingRss ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Ingest
                                        </button>
                                    </div>

                                    {rssError && (
                                        <div className="p-4 bg-rose-50 border border-rose-100 rounded-2xl flex items-center gap-4 animate-pop">
                                            <AlertTriangle size={20} className="text-rose-500 shrink-0" />
                                            <p className="text-[10px] font-black text-rose-700 tracking-widest leading-relaxed">{rssError}</p>
                                        </div>
                                    )}

                                    {channelMeta && (
                                        <div className="p-6 bg-white border border-indigo-100 rounded-3xl animate-fade-in flex flex-col md:flex-row gap-6 items-center shadow-lg shadow-indigo-900/5">
                                            <div className="w-24 h-24 rounded-2xl overflow-hidden shadow-md shrink-0 border-2 border-indigo-50">
                                                <img src={channelMeta.img} className="w-full h-full object-cover" />
                                            </div>
                                            <div className="flex-1 text-center md:text-left">
                                                <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                                                    <span className="p-1 bg-indigo-50 text-indigo-600 rounded-md"><Radio size={12}/></span>
                                                    <h5 className="font-black text-slate-800 text-sm tracking-tight">{channelMeta.title}</h5>
                                                </div>
                                                <p className="text-[10px] text-slate-500 font-bold line-clamp-2 leading-relaxed">"{channelMeta.desc}"</p>
                                                <div className="mt-5 flex flex-wrap gap-2 justify-center md:justify-start">
                                                    <button onClick={linkFullFeed} className="px-6 py-2.5 bg-slate-900 text-white rounded-xl font-black text-[9px] tracking-widest hover:bg-black transition-all shadow-md active:scale-[0.95]">Link Global Feed (Entire Series)</button>
                                                    <button 
                                                        onClick={handleBulkImportAll} 
                                                        disabled={isBulkImporting || rssEpisodes.length === 0}
                                                        className="px-6 py-2.5 bg-indigo-600 text-white rounded-xl font-black text-[9px] tracking-widest hover:bg-indigo-700 transition-all shadow-md active:scale-[0.95] flex items-center gap-2"
                                                    >
                                                        {isBulkImporting ? <Loader2 size={12} className="animate-spin" /> : <FastForward size={12}/>}
                                                        Bulk Ingest All ({rssEpisodes.length})
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {rssEpisodes.length > 0 && (
                                        <div className="space-y-4 animate-slide-up">
                                            <div className="flex items-center justify-between px-2">
                                                <span className="text-[9px] font-black text-indigo-400 tracking-widest flex items-center gap-2"><ListMusic size={14}/> Episodes Discovery: {rssEpisodes.length}</span>
                                                <button onClick={() => { setRssEpisodes([]); setChannelMeta(null); }} className="text-[8px] font-black text-rose-500 hover:underline">Clear Feed</button>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto pr-3 custom-scrollbar">
                                                {rssEpisodes.map((ep, idx) => (
                                                    <div key={idx} className="p-5 bg-white border border-indigo-100 rounded-[1.8rem] flex gap-4 items-center group hover:border-indigo-400 hover:shadow-xl transition-all shadow-sm">
                                                        <div className="w-14 h-14 rounded-2xl overflow-hidden shadow-sm shrink-0 relative">
                                                            <img src={ep.imageUrl} className="w-full h-full object-cover" />
                                                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                                <button 
                                                                    onClick={() => { setPreviewAudio({ url: ep.sourceUrl, title: ep.title }); setTimeout(() => previewRef.current?.play(), 100); }}
                                                                    className="p-2 bg-white text-slate-900 rounded-full shadow-2xl"
                                                                ><Play size={12} fill="currentColor" /></button>
                                                            </div>
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="font-black text-[10px] text-slate-800 truncate leading-tight">{ep.title}</p>
                                                            <div className="flex items-center gap-3 mt-1.5 opacity-60">
                                                                <span className="text-[8px] font-bold text-slate-400 flex items-center gap-1"><Calendar size={10}/> {new Date(ep.pubDate).toLocaleDateString()}</span>
                                                                <span className="text-[8px] font-bold text-indigo-600 flex items-center gap-1"><Clock size={10}/> {ep.duration}</span>
                                                            </div>
                                                            <div className="mt-3">
                                                                <button onClick={() => importEpisode(ep)} className="text-[8px] font-black text-indigo-600 hover:underline flex items-center gap-1.5 group/btn">
                                                                    Import Single <ChevronRight size={10} className="group-hover/btn:translate-x-1 transition-transform" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* MANUAL / METADATA CONFIGURATION */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                                <div className="space-y-8">
                                    <div className="space-y-3">
                                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Track Identity</label>
                                        <input 
                                            type="text" 
                                            value={selectedPodcast.title} 
                                            onChange={e => setSelectedPodcast({...selectedPodcast, title: e.target.value})} 
                                            className="w-full p-5 bg-slate-50 border border-slate-100 rounded-2xl font-black text-xl outline-none focus:bg-white transition-all shadow-inner tracking-tight text-slate-800" 
                                            placeholder="Episode Title..."
                                        />
                                    </div>
                                    
                                    <div className="space-y-3">
                                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Registry Narrative (Description)</label>
                                        <textarea 
                                            value={selectedPodcast.description} 
                                            onChange={e => setSelectedPodcast({...selectedPodcast, description: e.target.value})} 
                                            className="w-full p-6 bg-slate-50 border border-slate-100 rounded-3xl min-h-[160px] font-medium text-slate-700 outline-none focus:bg-white resize-none shadow-inner leading-relaxed" 
                                            placeholder="What is this lesson about?"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-8">
                                    <div className="space-y-3">
                                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1 flex items-center gap-2"><ImageIcon size={14} className="text-indigo-500" /> Branding Visual (URL)</label>
                                        <div className="relative group">
                                            <ImageIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-indigo-500 transition-colors" size={18} />
                                            <input 
                                                type="text" 
                                                value={selectedPodcast.imageUrl} 
                                                onChange={e => setSelectedPodcast({...selectedPodcast, imageUrl: e.target.value})} 
                                                className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-xs outline-none focus:bg-white" 
                                                placeholder="https://..."
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-6">
                                        <div className="space-y-3">
                                            <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1 flex items-center gap-2"><Clock size={14} className="text-indigo-400" /> Duration</label>
                                            <input 
                                                type="text" 
                                                value={selectedPodcast.duration} 
                                                onChange={e => setSelectedPodcast({...selectedPodcast, duration: e.target.value})} 
                                                className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl font-black text-xs outline-none focus:bg-white text-center tabular-nums" 
                                                placeholder="0:00" 
                                            />
                                        </div>
                                        <div className="space-y-3">
                                            <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1 flex items-center gap-2"><Layout size={14} className="text-medical-500" /> Sorting Weight</label>
                                            <input 
                                                type="number" 
                                                value={selectedPodcast.order} 
                                                onChange={e => setSelectedPodcast({...selectedPodcast, order: parseInt(e.target.value)})} 
                                                className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl font-black text-xs outline-none focus:bg-white text-center text-indigo-600 shadow-inner" 
                                            />
                                        </div>
                                    </div>

                                    <div className="p-8 bg-slate-900 rounded-[2.5rem] text-white flex flex-col items-center justify-center text-center shadow-2xl relative overflow-hidden group/viz">
                                        <div className="absolute inset-0 opacity-20 pointer-events-none bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.2)_0%,transparent_100%)]"></div>
                                        {selectedPodcast.imageUrl ? (
                                            <div className="w-32 h-32 rounded-3xl overflow-hidden shadow-2xl border-4 border-white/10 group-hover/viz:scale-105 transition-transform duration-500">
                                                <img src={selectedPodcast.imageUrl} className="w-full h-full object-cover" />
                                            </div>
                                        ) : (
                                            <div className="w-32 h-32 bg-white/5 rounded-3xl flex items-center justify-center border-4 border-dashed border-white/10 text-white/20">
                                                <ImageIcon size={48} />
                                            </div>
                                        )}
                                        <p className="mt-4 text-[9px] font-black tracking-[0.4em] text-indigo-400">Card Preview Image</p>
                                    </div>
                                </div>
                            </div>

                            {/* TRACK SOURCE PANEL */}
                            {!selectedPodcast.isRssFeed && (
                                <div className="space-y-8 animate-slide-up">
                                    <div className="h-px bg-slate-100"></div>
                                    <div className="space-y-6">
                                        <div className="flex items-center gap-3">
                                            <FileCode size={20} className="text-medical-600" />
                                            <h4 className="text-xl font-black text-slate-900 tracking-tight">Manual Source Logic</h4>
                                        </div>
                                        
                                        <div className="grid grid-cols-1 gap-8">
                                            <div className="space-y-3">
                                                <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1 flex items-center gap-2"><Code size={14}/> HTML Embed Code (Iframe)</label>
                                                <textarea 
                                                    value={selectedPodcast.embedHtml} 
                                                    onChange={e => setSelectedPodcast({...selectedPodcast, embedHtml: e.target.value, sourceType: 'embed'})} 
                                                    className="w-full p-6 bg-slate-950 text-emerald-400 font-mono text-[11px] rounded-3xl h-32 border-4 border-slate-900 outline-none focus:border-indigo-500/30 transition-all shadow-2xl" 
                                                    placeholder="<iframe src='https://open.spotify.com/embed/episode/...' ...></iframe>" 
                                                />
                                                <p className="text-[9px] text-slate-400 font-bold px-1 leading-relaxed">Required for Spotify, Apple, or Soundcloud standalone track embeds.</p>
                                            </div>

                                            {/* LIVE EMBED PREVIEW */}
                                            {selectedPodcast.embedHtml && (
                                                <div className="space-y-4">
                                                    <div className="flex items-center justify-between px-2">
                                                        <span className="text-[9px] font-black text-medical-600 tracking-widest flex items-center gap-1.5"><Eye size={14}/> Sandbox Render</span>
                                                        <button onClick={() => setSelectedPodcast({...selectedPodcast, embedHtml: ''})} className="text-[8px] font-black text-rose-500 hover:underline">Reset Embed</button>
                                                    </div>
                                                    <div className="w-full rounded-[2rem] overflow-hidden bg-slate-900 shadow-2xl border border-white/10 p-2 min-h-[150px] flex items-center justify-center">
                                                        <div 
                                                            className="w-full h-full"
                                                            dangerouslySetInnerHTML={{ __html: selectedPodcast.embedHtml }}
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                            <div className="space-y-3">
                                                <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1 flex items-center gap-2"><Volume2 size={14}/> Direct Audio URL (MP3)</label>
                                                <div className="relative group">
                                                    <FileAudio className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-indigo-500 transition-colors" size={18} />
                                                    <input 
                                                        type="text" 
                                                        value={selectedPodcast.sourceUrl} 
                                                        onChange={e => setSelectedPodcast({...selectedPodcast, sourceUrl: e.target.value, sourceType: 'rss'})} 
                                                        className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-100 rounded-2xl font-mono text-[10px] outline-none focus:bg-white text-indigo-600" 
                                                        placeholder="https://example.com/audio.mp3" 
                                                    />
                                                </div>
                                                <p className="text-[9px] text-slate-400 font-bold px-1 leading-relaxed">Direct path to a raw MP3 file. Will use the platform's native high-fidelity player.</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="pt-10 border-t border-slate-50 flex items-center justify-between bg-white sticky bottom-0 z-10 py-6">
                                <div className="flex items-center gap-4 px-6 py-2 bg-slate-50 rounded-2xl border border-slate-100">
                                    <button onClick={() => setSelectedPodcast({...selectedPodcast, isActive: !selectedPodcast.isActive})} className={`transition-all ${selectedPodcast.isActive ? 'text-indigo-600' : 'text-slate-300'}`}>
                                        {selectedPodcast.isActive ? <ToggleRight size={44} /> : <ToggleLeft size={44} />}
                                    </button>
                                    <div>
                                        <p className="text-[10px] font-black text-slate-800 tracking-tight">Publication Status</p>
                                        <p className={`text-[8px] font-black ${selectedPodcast.isActive ? 'text-emerald-600' : 'text-rose-500'}`}>{selectedPodcast.isActive ? 'VISIBLE TO STUDENTS' : 'DRAFT/HIDDEN'}</p>
                                    </div>
                                </div>
                                <div className="flex gap-4">
                                    <button onClick={() => setSelectedPodcast(null)} className="px-8 py-4 bg-white text-slate-500 font-black text-[10px] tracking-widest rounded-2xl border border-slate-200 hover:bg-slate-50 transition-all">Discard Draft</button>
                                    <button 
                                        onClick={handleSavePodcast} 
                                        disabled={isSaving || !selectedPodcast.title} 
                                        className="px-14 py-4 bg-slate-900 text-white rounded-2xl font-black text-[10px] tracking-widest shadow-2xl hover:bg-black transition-all active:scale-[0.95] disabled:opacity-50 border-b-4 border-black flex items-center gap-3"
                                    >
                                        {isSaving ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle size={18} />} 
                                        Commit to Registry
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="xl:col-span-8 flex flex-col items-center justify-center bg-slate-50 rounded-[3rem] border-4 border-dashed border-slate-200 opacity-60">
                        <div className="w-24 h-24 bg-white rounded-[2.5rem] flex items-center justify-center text-slate-200 shadow-xl mb-8 group-hover:scale-[1.1] transition-transform">
                            <Mic2 size={48}/>
                        </div>
                        <h4 className="text-xl font-black text-slate-900 tracking-tighter">Audio Workspace Empty</h4>
                        <p className="font-black text-slate-400 tracking-[0.3em] text-[10px] mt-2">Provision a new asset or select an existing lesson to edit</p>
                    </div>
                )}
            </div>
        </div>
    );
};

const ToggleLeft = ({size}: {size: number}) => (
    <svg width={size} height={size/2} viewBox="0 0 48 24" fill="none"><rect width="48" height="24" rx="12" fill="currentColor" fillOpacity="0.1"/><circle cx="12" cy="12" r="8" fill="white" shadow-lg="true"/></svg>
);

const ToggleRight = ({size}: {size: number}) => (
    <svg width={size} height={size/2} viewBox="0 0 48 24" fill="none"><rect width="48" height="24" rx="12" fill="currentColor"/><circle cx="36" cy="12" r="8" fill="white"/></svg>
);
